/**
 * Entity Router — public hook surface for all entity reads.
 *
 * Consumers never import a backing store directly. They call:
 *
 *   useEntity(id)                    → single entity, falls through space → global → byId cache.
 *   useEntitiesBySpace(spaceId)      → all entities cached for a space.
 *   useEntitiesByType(type)          → entities of a specific type, routed by registry.
 *   useGlobalEntities()              → every global entity.
 *   useDisplayableEntities(spaceId)  → space entities filtered through entityVisibility.
 *   useEntityById(id)                → re-exported from entityByIdCache (cross-space single-id).
 *
 * Non-hook accessors live on `entityRegistry` (registry.getById, getByType,
 * upsert, deleteEntity) — used by system commands and the live-update bus.
 *
 * Routing rules live in `entityRegistry.ts`. This file contains only React-
 * facing selectors built on top of the backing stores.
 */

import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useSpaceEntityStore, useSpaceEntities } from './spaceEntityStore';
import {
  useGlobalEntityStore,
  useGlobalEntitiesByType,
  useGlobalEntities as useAllGlobalEntities,
} from './globalEntityStore';
import { useEntityByIdCache } from './entityByIdCache';
import { entityRegistry, resolveScope } from './entityRegistry';
import { buildDisplayableEntities } from '../utils/entityVisibility';
import { HIDDEN_ENTITY_TYPES } from '../utils/entityVisibility';
import { subscribeToOrgNodes, getStableOrgNodes, subscribeToOrgConnectionStatus, getOrgConnectionStatus, subscribeToOrgConnectionSynced, getOrgConnectionSynced, type OrgNodeMeta, type OrgConnectionStatus } from './orgYDocStore';
import type { BaseEntityType } from '../types/entities';

/** Re-exports — keep all entity-read primitives behind a single import. */
export { useSpaceEntities, useSpaceEntity } from './spaceEntityStore';
export {
  useGlobalEntities as useAllGlobalEntities,
  useGlobalEntitiesByType,
  useConnectedIntegrations,
} from './globalEntityStore';
export { useEntityById } from './entityByIdCache';
export { entityRegistry, resolveScope } from './entityRegistry';

const EMPTY_ENTITIES: BaseEntityType[] = [];

/**
 * Reactive aggregate of every entity across every backing store.
 *
 * Replaces the legacy `useAllEntities()`. Most callers
 * actually want a narrower scope (`useEntitiesBySpace`, `useEntitiesByType`,
 * `useGlobalEntities`); this hook is for legacy filter-everything code that
 * we couldn't migrate to a tighter selector. Returns referentially stable
 * empty arrays when nothing is cached.
 *
 * Implementation note: the zustand selector must return a stable identity so
 * React's `useSyncExternalStore` snapshot cache holds across renders — see
 * the matching pattern in `EntitySidebar/hooks/useEntityData.ts`. We
 * subscribe to the space store's `bumpVersion` (a primitive that ticks on
 * every cache mutation) and rebuild the deduped array inside `useMemo`.
 */
export function useAllEntities(): BaseEntityType[] {
  const spaceBump = useSpaceEntityStore((s) => s.bumpVersion);
  const globalEntities = useAllGlobalEntities();

  const spaceEntities = useMemo(() => {
    void spaceBump;
    const caches = useSpaceEntityStore.getState().caches;
    let total = 0;
    for (const entry of caches.values()) total += entry.entities.length;
    if (total === 0) return EMPTY_ENTITIES;
    const out: BaseEntityType[] = [];
    const seen = new Set<string>();
    for (const entry of caches.values()) {
      for (const e of entry.entities) {
        if (!e?._id || seen.has(e._id)) continue;
        seen.add(e._id);
        out.push(e);
      }
    }
    return out.length === 0 ? EMPTY_ENTITIES : out;
  }, [spaceBump]);

  return useMemo(() => {
    if (spaceEntities.length === 0 && globalEntities.length === 0) return EMPTY_ENTITIES;
    if (globalEntities.length === 0) return spaceEntities;
    if (spaceEntities.length === 0) return globalEntities;
    const out: BaseEntityType[] = [];
    const seen = new Set<string>();
    for (const e of spaceEntities) {
      if (!e?._id || seen.has(e._id)) continue;
      seen.add(e._id);
      out.push(e);
    }
    for (const e of globalEntities) {
      if (!e?._id || seen.has(e._id)) continue;
      seen.add(e._id);
      out.push(e);
    }
    return out;
  }, [spaceEntities, globalEntities]);
}

/**
 * Whether the global entity store has finished its first load. Used by
 * gating UI ("don't render an empty state until we know what we have").
 *
 * Replaces the legacy `useEntitySystemReady()` and
 * `useEntitySystemReady()` patterns.
 */
export function useEntitySystemReady(): boolean {
  return useGlobalEntityStore((s) => s.lifecycle === 'ready');
}

/**
 * Single-entity read with cross-store fallback.
 *
 * Resolution order:
 *   1. spaceEntityStore (any cached space).
 *   2. globalEntityStore.
 *   3. entityByIdCache (which itself falls through to IDB then REST).
 *
 * The hook is reactive — any of the three stores mutating the entity will
 * trigger a re-render. When the entity is missing everywhere, we kick off
 * a fetch via the byId cache exactly once and return undefined until it
 * resolves.
 */
export function useEntity(id: string | null | undefined): BaseEntityType | undefined {
  // Subscribe to spaceEntityStore — first space cache that has it wins.
  const fromSpace = useSpaceEntityStore((s) => {
    if (!id) return undefined;
    for (const entry of s.caches.values()) {
      const e = entry.entityById.get(id);
      if (e) return e;
    }
    return undefined;
  });

  // Subscribe to globalEntityStore.
  const fromGlobal = useGlobalEntityStore((s) => (id ? s.byId.get(id) : undefined));

  // Subscribe to byId cache as a last resort.
  const fromById = useEntityByIdCache((s) => (id ? s.entries.get(id) : undefined));

  // Kick off a byId fetch if nothing is cached anywhere. Side effects must
  // run inside useEffect — calling fetch during render violates the rules of
  // hooks and triggers double-fetches under React StrictMode.
  const missing = !!id && !fromSpace && !fromGlobal && !fromById;
  useEffect(() => {
    if (!missing || !id) return;
    const cacheState = useEntityByIdCache.getState();
    if (cacheState.inflight.has(id)) return;
    cacheState.fetch(id).catch(() => {});
  }, [id, missing]);

  return fromSpace ?? fromGlobal ?? fromById;
}

/**
 * Type-filtered list, routed via the registry.
 *
 * - Global types (Space, Integration*, MCPConnection, Org) → globalEntityStore.
 * - Space-scoped types (everything else) → filter spaceEntityStore caches by
 *   entityType across every cached space.
 *
 * App-owned types (Mail.*, Tasks.*, Bank.*, Automation.*, Database.*, Contact)
 * default to the space scope at the registry level, but the canonical source
 * for those is the dedicated app store (mailAppStore, tasksAppStore, ...) —
 * prefer those store's own hooks when you need richer derived state
 * (folder counts, drafts, etc.).
 */
export function useEntitiesByType(entityType: string): BaseEntityType[] {
  const scope = resolveScope(entityType);

  const fromGlobal = useGlobalEntitiesByType(scope === 'global' ? entityType : '__none__');

  // For space-scoped types, gather across all cached spaces. Subscribe to
  // `bumpVersion` (stable primitive) and build the array in useMemo to keep
  // the snapshot cache stable; see useAllEntities for why.
  const spaceBump = useSpaceEntityStore((s) => (scope === 'space' ? s.bumpVersion : 0));
  const spaceMatches = useMemo(() => {
    if (scope !== 'space') return EMPTY_ENTITIES;
    void spaceBump;
    const caches = useSpaceEntityStore.getState().caches;
    const out: BaseEntityType[] = [];
    const seen = new Set<string>();
    for (const entry of caches.values()) {
      for (const e of entry.entities) {
        if (e.entityType !== entityType) continue;
        const id = e._id;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push(e);
      }
    }
    return out.length === 0 ? EMPTY_ENTITIES : out;
  }, [scope, entityType, spaceBump]);

  return scope === 'global' ? fromGlobal : spaceMatches;
}

/**
 * All space-scoped entities for the active space, filtered through the
 * canonical visibility predicate (`entityVisibility.buildDisplayableEntities`).
 *
 * Hook-shaped equivalent of the registry's "give me everything visible in this
 * space" query, with the canonical visibility predicate already applied.
 */
export function useDisplayableEntities(
  spaceId: string | null | undefined,
): BaseEntityType[] {
  const entities = useSpaceEntities(spaceId);
  return useMemo(() => buildDisplayableEntities(entities), [entities]);
}

/**
 * All displayable entities across every backing store (space caches + global
 * store). Used by legacy callers that pre-date space scoping — sidebars,
 * search, file popups — where filtering by active space happens elsewhere.
 *
 * Prefer `useDisplayableEntities(spaceId)` whenever the consumer is rendering
 * inside a known space; this hook is intentionally broader.
 */
export function useAllDisplayableEntities(): BaseEntityType[] {
  const entities = useAllEntities();
  return useMemo(() => buildDisplayableEntities(entities), [entities]);
}

/**
 * All trashed space-scoped entities for the active space.
 *
 * Note: cross-space trash views (RiverDriveApp, OpenDialog) cannot use this
 * hook — they fetch from REST directly via `entityService.queryEntities({
 * trashed: true })` because trashed entities are filtered out of cached
 * space slices.
 */
export function useTrashedEntitiesForSpace(
  spaceId: string | null | undefined,
): BaseEntityType[] {
  // We pull the raw cache (including trashed) and filter ourselves. Note the
  // current spaceEntityStore filters trashed at fetch time; this is mostly a
  // forward-compatible no-op selector that matches the future schema where
  // trashed entities stay in the cache.
  const entities = useSpaceEntities(spaceId);
  return useMemo(
    () => entities.filter((e) => (e as any).trashed === true && (e as any).hidden !== true),
    [entities],
  );
}

/**
 * Convenience — read-only namespace exposing the registry's lookup primitives
 * for non-hook code (system commands, app stores). Mutations live on
 * `entityActions` (orchestrated) or `entityRegistry` (raw routing) — keeping
 * the router as a reads-only surface preserves a clear write path.
 */
export const entityRouter = {
  getById: entityRegistry.getById,
  getByType: entityRegistry.getByType,
  resolveScope: entityRegistry.resolveScope,
};

// ── Global filesystem layer ───────────────────────────────────────────────────

export { type OrgNodeMeta } from './orgYDocStore';

/**
 * Reactive Map of lightweight entity metadata from the org Y.Doc nodes map.
 * Contains { entityId, name, icon?, entityType } for every entity in the
 * org's dataspace — including entities from spaces not yet visited this session
 * (no full body loaded). Updates whenever the org Y.Doc nodes map changes.
 *
 * Use this for instant fuzzy-search over all entity names, global file-tree
 * stubs, or anything that needs "is this entity a Space?" without a full fetch.
 */
export function useOrgNodes(): Map<string, OrgNodeMeta> {
  return useSyncExternalStore(subscribeToOrgNodes, getStableOrgNodes);
}

/**
 * The org-room WS connection status ('connecting' | 'connected' | 'disconnected').
 * Drives lightweight "reconnecting…" / "offline" UI so a user isn't silently
 * looking at a possibly-stale sidebar tree with no indication anything is wrong.
 */
export function useOrgConnectionStatus(): OrgConnectionStatus {
  return useSyncExternalStore(subscribeToOrgConnectionStatus, getOrgConnectionStatus);
}

/**
 * Whether the org room has completed a Y.js sync since its current connection
 * attempt. `useOrgConnectionStatus() === 'connected'` alone is not sufficient
 * to trust the data on screen — a "connected zombie" socket reports connected
 * while never completing the sync handshake (see wsReconnectCoordinator's
 * zombie detection). Combine both via `useOrgConnectionHealth()` below for any
 * indicator meant to answer "can I trust what's on screen right now".
 */
export function useOrgConnectionSynced(): boolean {
  return useSyncExternalStore(subscribeToOrgConnectionSynced, getOrgConnectionSynced);
}

export interface OrgConnectionHealth {
  status: OrgConnectionStatus;
  synced: boolean;
  /** True only when both dimensions agree the room is live and trustworthy. */
  healthy: boolean;
}

/** Combined connection-status + synced view — the ground truth for "is org data live right now". */
export function useOrgConnectionHealth(): OrgConnectionHealth {
  const status = useOrgConnectionStatus();
  const synced = useOrgConnectionSynced();
  return { status, synced, healthy: status === 'connected' && synced };
}

/**
 * `OrgConnectionStatus`-shaped value for display purposes, folding the synced
 * dimension into 'connecting' so a "connected zombie" reads exactly like a
 * socket that's still finishing its handshake — both mean "don't trust this
 * yet" and get the same "Reconnecting…" treatment rather than a false
 * all-clear. Used by both `OrgConnectionBanner` (sidebar) and the global
 * desktop-layout indicator so they never disagree about what's healthy.
 */
export function useOrgConnectionDisplayStatus(): OrgConnectionStatus {
  const { status, synced } = useOrgConnectionHealth();
  if (status === 'connected' && !synced) return 'connecting';
  return status;
}

/**
 * Globally displayable entities — the union of:
 *   1. All cached entity bodies (every space/global store that has loaded).
 *   2. Lightweight stubs (name + entityType from the org nodes map) for any
 *      entity ID present in the nodes map but not yet in any body cache.
 *
 * Stubs are compatible with BaseEntityType and pass isVisibleEntity(). When
 * a full body later loads, it replaces the stub in the result array on the
 * next render.
 *
 * This hook powers the global Drive, sidebar, and search — surfaces that need
 * to show ALL org entities without eagerly fetching every body.
 */
export function useGlobalDisplayableEntities(): BaseEntityType[] {
  const orgNodes = useOrgNodes();

  // Cached full bodies across all loaded spaces.
  const spaceBump = useSpaceEntityStore((s) => s.bumpVersion);
  const globalEntities = useAllGlobalEntities();

  return useMemo(() => {
    // Gather all cached entity IDs so we know which nodes map entries need stubs.
    const cached = new Map<string, BaseEntityType>();
    const spacesCaches = useSpaceEntityStore.getState().caches;
    for (const entry of spacesCaches.values()) {
      for (const e of entry.entities) {
        if (e?._id) cached.set(e._id, e);
      }
    }
    for (const e of globalEntities) {
      if (e?._id) cached.set(e._id, e);
    }

    // Start with all cached bodies, filtered to displayable.
    const out: BaseEntityType[] = [];
    const seen = new Set<string>();
    for (const [, e] of cached) {
      if (!e?._id) continue;
      seen.add(e._id);
      // Apply visibility filter inline (avoids a second pass).
      if (
        (e as any).trashed === true ||
        HIDDEN_ENTITY_TYPES.has(e.entityType) ||
        e.entityType?.startsWith('Integration.') ||
        (e as any).hidden === true ||
        (e.skeleton as any)?.hidden === true
      ) continue;
      out.push(e);
    }

    // Add lightweight stubs for entities in the nodes map that have no cached body.
    for (const [entityId, meta] of orgNodes) {
      if (seen.has(entityId)) continue;
      if (!meta.entityType) continue;
      // Apply the same visibility gates as isVisibleEntity().
      if (
        HIDDEN_ENTITY_TYPES.has(meta.entityType) ||
        meta.entityType.startsWith('Integration.')
      ) continue;
      // Stub — only name/entityType/icon are populated; full body not loaded yet.
      out.push({
        _id: entityId,
        id: entityId,
        name: meta.name,
        entityType: meta.entityType,
        icon: meta.icon,
        skeleton: {},
        created_at: '',
        updated_at: '',
      } as unknown as BaseEntityType);
    }

    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgNodes, spaceBump, globalEntities]);
}
